import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ArrowLeft, Wallet, Gamepad2, Receipt, PieChart, ExternalLink, Cpu } from 'lucide-react';

const TYPE_COLORS = {
  purchase: 'bg-primary/15 text-primary border-primary/30',
  purchase_system: 'bg-violet-500/15 text-violet-400 border-violet-500/30',
  repair: 'bg-secondary/15 text-secondary border-secondary/30',
  transport: 'bg-muted text-muted-foreground border-border',
};

const TYPE_BARS = {
  purchase: 'bg-primary',
  purchase_system: 'bg-violet-500',
  repair: 'bg-secondary',
  transport: 'bg-muted-foreground/50',
};

const InvestorDetail = () => {
  const { id } = useParams();
  const { t, API } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchInvestor = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/investors/${encodeURIComponent(id)}`);
        setData(res.data);
      } catch (e) {
        if (e.response?.status === 404) setNotFound(true);
        console.error('Failed to fetch investor:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchInvestor();
  }, [API, id]);

  const formatCurrency = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v || 0);
  const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : '-');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="space-y-6">
        <Button variant="outline" onClick={() => navigate('/investors')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> {t('investors.backToList')}
        </Button>
        <Card className="bezel p-12 text-center">
          <p className="text-muted-foreground">{t('investors.notFound')}</p>
        </Card>
      </div>
    );
  }

  const maxCabinet = data.cabinets.reduce((m, c) => Math.max(m, c.amount), 0) || 1;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="investor-detail-page">
      <Button variant="outline" onClick={() => navigate('/investors')}>
        <ArrowLeft className="h-4 w-4 mr-2" /> {t('investors.backToList')}
      </Button>

      {/* En-tête fiche */}
      <Card className="bezel border-primary/25">
        <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center gap-6">
          <Avatar className="w-20 h-20">
            <AvatarImage src={data.avatar || undefined} />
            <AvatarFallback className="bg-primary/10 text-primary text-2xl font-bold">
              {data.nickname?.slice(0, 2).toUpperCase() || '??'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <h1 className="text-3xl font-heading font-bold tracking-tight">{data.nickname}</h1>
            <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground font-mono">
              <span className="flex items-center gap-1"><Gamepad2 className="w-4 h-4" /> {data.cabinets_count} {t('investors.cabinets')}</span>
              <span className="flex items-center gap-1"><Receipt className="w-4 h-4" /> {data.operations_count} {t('investors.operations')}</span>
            </div>
          </div>
          <div className="text-right">
            <p className="eyebrow text-[10px] text-muted-foreground">{t('investors.totalInvested')}</p>
            <p className="font-display text-3xl font-bold text-primary text-glow-amber tabular-nums">{formatCurrency(data.total_invested)}</p>
            <p className="text-sm font-mono text-muted-foreground tabular-nums mt-1">{data.percentage}% {t('investors.shareOfTotal').toLowerCase()}</p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Répartition par type */}
        <Card className="bezel">
          <CardHeader>
            <CardTitle className="eyebrow text-[10px] text-muted-foreground flex items-center gap-2">
              <PieChart className="w-4 h-4 text-primary" /> {t('investors.byType')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {Object.entries(data.by_type).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('dashboard.noData')}</p>
            ) : (
              Object.entries(data.by_type).map(([type, amount]) => {
                const pct = data.total_invested ? (amount / data.total_invested) * 100 : 0;
                return (
                  <div key={type}>
                    <div className="flex justify-between text-sm mb-1">
                      <Badge variant="outline" className={`text-[11px] ${TYPE_COLORS[type] || ''}`}>{t(`operations.${type}`)}</Badge>
                      <span className="font-mono font-medium tabular-nums">{formatCurrency(amount)}</span>
                    </div>
                    <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                      <div className={`h-full transition-all duration-500 ${TYPE_BARS[type] || 'bg-primary'}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* Investissement par borne */}
        <Card className="bezel lg:col-span-2">
          <CardHeader>
            <CardTitle className="eyebrow text-[10px] text-muted-foreground flex items-center gap-2">
              <Gamepad2 className="w-4 h-4 text-primary" /> {t('investors.perCabinet')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {data.cabinets.map((c) => (
              <div
                key={c.cabinet_id}
                className="flex items-center gap-3 group cursor-pointer"
                onClick={() => navigate(`/cabinets/${c.cabinet_id}`)}
                title={t('investors.viewCabinet')}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium truncate flex items-center gap-1">
                      {c.cabinet_name}
                      <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </span>
                    <span className="font-mono font-medium tabular-nums">{formatCurrency(c.amount)}</span>
                  </div>
                  <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${(c.amount / maxCabinet) * 100}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Détail des opérations */}
      <Card className="bezel">
        <CardHeader>
          <CardTitle className="eyebrow text-[10px] text-muted-foreground flex items-center gap-2">
            <Receipt className="w-4 h-4 text-primary" /> {t('investors.operationsList')}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {data.operations.map((op) => (
              <div key={op.operation_id} className="p-4 flex items-center justify-between gap-4 hover:bg-muted/40 transition-colors">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`text-[11px] uppercase ${TYPE_COLORS[op.operation_type] || ''}`}>
                      {t(`operations.${op.operation_type}`)}
                    </Badge>
                    <button
                      className="text-sm font-medium hover:underline truncate"
                      onClick={() => navigate(`/cabinets/${op.cabinet_id}`)}
                    >
                      {op.cabinet_name}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">{op.description}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-mono font-bold tabular-nums">{formatCurrency(op.amount)}</p>
                  <p className="text-xs text-muted-foreground font-mono tabular-nums">{formatDate(op.date)}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Participation aux pièces détachées (indicatif, hors total) */}
      {data.spare_parts?.length > 0 && (
        <Card className="bezel">
          <CardHeader>
            <CardTitle className="eyebrow text-[10px] text-muted-foreground flex items-center gap-2">
              <Cpu className="w-4 h-4 text-primary" /> {t('investors.spareParticipation')}
              <span className="ml-auto font-display text-base text-primary tabular-nums normal-case tracking-normal">{formatCurrency(data.spare_total)}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground italic">{t('investors.spareParticipationNote')}</p>
            <div className="divide-y">
              {data.spare_parts.map((sp) => (
                <div
                  key={sp.spare_part_id}
                  className="py-2.5 flex items-center justify-between gap-4 cursor-pointer hover:bg-muted/40 -mx-2 px-2 rounded transition-colors"
                  onClick={() => navigate('/spare-parts')}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{sp.name}</p>
                    <p className="text-xs text-muted-foreground font-mono">
                      {sp.quantity_available}/{sp.quantity_purchased} {t('spareParts.quantityAvailable').toLowerCase()}
                    </p>
                  </div>
                  <span className="font-mono font-bold tabular-nums shrink-0">{formatCurrency(sp.amount)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default InvestorDetail;
